import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocale } from "../../contexts/localeContext";
import { Lookup } from "../../models/Lookup";
import { ListQuery } from "../../models/ListQuery";
import { TableContextProvider } from "../../contexts/table/TableContextProvider";
import TableTab from "../tabs/table-tab";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { ListData } from "../../models/ListData";
import { DEF_PG_SIZE_SUB } from "../../constants/maximum-page-size";
import NotificationAuditColumns from "../../models/ColumnDefinitions/NotificationAuditColumns";
import NotificationAuditSearch from "../../models/search/NotificationAuditSearch";
import { getAuditForNotification } from "../../services/auditService";
import { appendFilter } from '../../utilities/filters';
import { getDateForPreviousThirtyDays, getTodayDate } from "../../utilities/DateUtils";
import { REPORTS_AUDIT } from "../../constants/menu-types";
import { showError } from "../../services/utilities/ServiceError";
import { toast } from 'react-toastify';


interface Props {
    notificationId?: number;
}

const NotificationAuditTab: FC<Props> = ({ notificationId = 0 }) => {
    const { translate } = useLocale();
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const userLoginId = useSelector((state: any) => (state.userInfo.id))

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }

    useEffect(() => {
        dataRefresh(getTableData);
    }, [notificationId, translate])

    /**
     * Filtered data fetch method for the table. Filters by notificationId and 'NOTIFICATIONS' audit sub type.
     * @param listParameters 
     * @returns 
     */
    const getTableData = (listParameters?) => {
        return getAuditForNotification(notificationId.toString(), {
            ...listParameters,
            filters: appendFilter(notificationId, 'VRule', 'adEntityId', listParameters?.filters)
        },
            translate)
    }

    // method that is builds uneditable filter that contains two dates (todays date and previous 30 days)
    const buildDateFilter = (setDetails) => {
        try {
            const previousThirtyDaysDate = getDateForPreviousThirtyDays();
            const todaysDate = getTodayDate();
            const date = `${previousThirtyDaysDate};${todaysDate}`;

            const customFilter = [{
                key: "auditSession.dateOnly",
                label: "Date",
                clearPolicy: "locked",
                options: [
                    { label: date, value: date },
                ],
                selected: [
                    { label: date, value: date },
                ],
            }];

            setDetails(userLoginId, REPORTS_AUDIT, customFilter);
        } catch (error) {
            showError(error, toast);
        }
    };

    return (<TableContextProvider
        dataMethod={(listParameters) => getTableData(listParameters)}
        initialiser={(setDetails) => buildDateFilter(setDetails)}
        manualDataRefresh={manualDataRefresh}
        tableDescriptor={NotificationAuditColumns}
        searchDescriptor={NotificationAuditSearch}
        useFilter
        useSearch
        usePagination
        maxPageSize={DEF_PG_SIZE_SUB}
        useTable
        lookup={lookup}>
        <TableTab
            title={''}
            reportTitle={translate.byKey(TranslationKeys.audit)}
            selectRows={false}
            showFilterMenu={false}
            disableFilterToggling={true}
            cyName="notificationAudit"
        >
        </TableTab>
    </TableContextProvider >
    );
};

export default NotificationAuditTab;
